import { css } from "@emotion/css";
import { CircularProgress, Collapse } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ToolCallEvent, ToolResponseEvent } from "@octopusdeploy/octopus-server-client";
import { useEffect, useRef, useState } from "react";
const getToolCallDisplayName = (toolCall: ToolCallEvent): string => {
    // Hard-coded mapping for GetServerTaskRaw tool
    if (toolCall.functionName === "GetServerTaskRaw") {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const args = toolCall.arguments as {
            retrievalMode?: string;
        };
        const mode = args.retrievalMode || "SmartSummary";
        switch (mode) {
            case "SmartSummary":
                return "Analyzing task logs";
            case "Search":
                return "Searching task logs";
            case "LineRange":
                return "Reading specific log lines";
            case "Tail":
                return "Reading recent log entries";
            case "LogLevel":
                return "Filtering logs by severity";
            default:
                return "Retrieving task logs";
        }
    }
    else if (toolCall.functionName === "GetDeploymentStep") {
        return "Fetching deployment step details";
    }
    else if (toolCall.functionName === "GetDeploymentProcessSnapshot") {
        return "Fetching deployment process snapshot";
    }
    else if (toolCall.functionName === "GetRunbookProcessSnapshot") {
        return "Fetching runbook process snapshot";
    }
    else if (toolCall.functionName === "GetRunbookStep") {
        return "Fetching runbook step details";
    }
    else if (toolCall.functionName === "GetChildServerTasks") {
        return "Checking for child deployments";
    }
    return `${toolCall.functionName}`;
};
type ToolCallItemProps = {
    toolCall: ToolCallEvent;
    toolResponse?: ToolResponseEvent;
};
const ToolCallItem = ({ toolCall, toolResponse }: ToolCallItemProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpanded = () => setIsExpanded((prev) => !prev);
    const isComplete = toolResponse !== undefined;
    return (<div className={itemStyles.container}>
            <div className={itemStyles.header} onClick={toggleExpanded} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleExpanded()}>
                <div className={itemStyles.headerContent}>
                    {isComplete ? <CheckCircleIcon size={16} color={themeTokens.color.icon.success}/> : <CircularProgress size="small" aria-label="In progress"/>}
                    <span className={itemStyles.toolName}>{getToolCallDisplayName(toolCall)}</span>
                    {isExpanded ? <ChevronDownIcon size={16} color={themeTokens.color.icon.secondary}/> : <ChevronRightIcon size={16} color={themeTokens.color.icon.secondary}/>}
                </div>
            </div>

            <Collapse in={isExpanded}>
                <div className={itemStyles.content}>
                    <div className={itemStyles.section}>
                        <div className={itemStyles.sectionTitle}>Arguments</div>
                        <pre className={itemStyles.codeBlock}>{JSON.stringify(toolCall.arguments, null, 2)}</pre>
                    </div>

                    {toolResponse && (<div className={itemStyles.section}>
                            <div className={itemStyles.sectionTitle}>Response</div>
                            <div className={itemStyles.responseContent}>
                                <pre className={itemStyles.codeBlock}>{formatResponse(toolResponse.response)}</pre>
                            </div>
                        </div>)}

                    {!toolResponse && (<div className={itemStyles.section}>
                            <div className={itemStyles.loadingText}>Executing tool...</div>
                        </div>)}
                </div>
            </Collapse>
        </div>);
};
type ToolCallGroupProps = {
    toolCalls: ToolCallEvent[];
    toolResponseMap: Map<string, ToolResponseEvent>;
    isStreaming: boolean;
};
export const ToolCallGroup = ({ toolCalls, toolResponseMap, isStreaming }: ToolCallGroupProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const prevStreamingRef = useRef(isStreaming);
    // Auto-collapse when streaming transitions from true to false
    useEffect(() => {
        if (prevStreamingRef.current && !isStreaming) {
            setIsExpanded(false);
        }
        prevStreamingRef.current = isStreaming;
    }, [isStreaming]);
    const toggleExpanded = () => setIsExpanded((prev) => !prev);
    const allComplete = toolCalls.length > 0 && toolCalls.every((tc) => toolResponseMap.has(tc.id));
    const showDone = !isStreaming && allComplete;
    return (<div className={groupStyles.container}>
            <div className={groupStyles.header} onClick={toggleExpanded} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleExpanded()}>
                {isExpanded ? <ChevronDownIcon size={16} color={themeTokens.color.icon.secondary}/> : <ChevronRightIcon size={16} color={themeTokens.color.icon.secondary}/>}
                <span className={groupStyles.headerLabel}>Gathering context</span>
            </div>

            <Collapse in={isExpanded}>
                <div className={groupStyles.itemsContainer}>
                    {toolCalls.map((toolCall) => (<ToolCallItem key={toolCall.id} toolCall={toolCall} toolResponse={toolResponseMap.get(toolCall.id)}/>))}

                    {showDone && (<div className={groupStyles.doneRow}>
                            <CheckCircleIcon size={16} color={themeTokens.color.icon.success}/>
                            <span className={groupStyles.doneText}>Done</span>
                        </div>)}
                </div>
            </Collapse>
        </div>);
};
function formatResponse(input: string): string {
    let result = input;
    try {
        // Try to parse as JSON for better formatting
        const parsed = JSON.parse(input);
        result = JSON.stringify(parsed, null, 2);
    }
    catch {
        // If not JSON, return as is (truncated if too long)
        if (input.length > 500) {
            result = `${input.substring(0, 500)}
... (truncated)`;
        }
    }
    return result.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
}
const groupStyles = {
    container: css({
        marginBottom: space[8],
    }),
    header: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        padding: `${space[6]} 0`,
        cursor: "pointer",
        userSelect: "none",
        transition: "opacity 0.2s",
        "&:hover": {
            opacity: 0.8,
        },
    }),
    headerLabel: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    itemsContainer: css({
        borderLeft: `2px solid ${themeTokens.color.border.default}`,
        marginLeft: space[8],
        paddingLeft: space[12],
    }),
    doneRow: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        padding: `${space[4]} 0`,
    }),
    doneText: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
    }),
};
const itemStyles = {
    container: css({
        marginBottom: space[4],
    }),
    header: css({
        display: "flex",
        alignItems: "center",
        padding: `${space[4]} 0`,
        cursor: "pointer",
        userSelect: "none",
        transition: "opacity 0.2s",
        "&:hover": {
            opacity: 0.8,
        },
    }),
    headerContent: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        flex: 1,
        minWidth: 0,
    }),
    toolName: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
    content: css({
        paddingLeft: space[24],
        paddingBottom: space[8],
        "@media (max-width: 1024px)": {
            paddingLeft: space[16],
            paddingBottom: space[6],
        },
    }),
    section: css({
        marginTop: space[12],
        "&:first-of-type": {
            marginTop: 0,
        },
        "@media (max-width: 1024px)": {
            marginTop: space[8],
        },
    }),
    sectionTitle: css({
        font: text.body.bold.small,
        color: themeTokens.color.text.secondary,
        marginBottom: space[6],
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
            marginBottom: space[4],
        },
    }),
    codeBlock: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        padding: space[12],
        borderRadius: borderRadius.small,
        fontSize: fontSize.small,
        fontFamily: "monospace",
        overflow: "auto",
        maxHeight: "300px",
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        "@media (max-width: 1024px)": {
            padding: space[8],
            fontSize: fontSize.xSmall,
            maxHeight: "200px",
        },
    }),
    responseContent: css({
        position: "relative",
    }),
    loadingText: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
        fontStyle: "italic",
        padding: space[8],
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
            padding: space[6],
        },
    }),
};
